import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { SegmentMeter } from "@/components/SegmentMeter";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UploadCloud, ArrowLeft, ArrowRight, Check, FileSpreadsheet, MessageSquarePlus, Mail, Undo2, Info, ChevronLeft, ChevronRight, Scissors, ArrowDownUp, Trash2, PhoneOff, ChevronUp, ChevronDown, ChevronsUpDown, Image as ImageIcon, Crown, Eye } from "lucide-react";
import { LogoCropper } from "./LogoCropper";
import { MessageQualityHint } from "./MessageQualityHint";
import { smartEncode } from "@/lib/smsQuality";
import { V } from "@/lib/vocab";

const flagMetaFactory = () => ({
  invalid_phone: { label: "bad phone", cls: "bg-amber-50 text-amber-700 border-amber-200", why: "The phone number looks too short / not a valid mobile. It'll import without a number unless you fix it." },
  no_contact: { label: "no contact", cls: "bg-orange-50 text-orange-700 border-orange-200", why: `No valid phone AND no email — there's no way to message this ${V.lc("person")}, so it's skipped by default. Add a phone or email and it'll re-include itself.` },
  duplicate: { label: "duplicate", cls: "bg-amber-50 text-amber-700 border-amber-200", why: "This same person (matching name or phone) already appears earlier in this file — likely listed twice." },
  missing_name: { label: "no name", cls: "bg-amber-50 text-amber-700 border-amber-200", why: "This row has no name. Add one, or it imports as a nameless contact." },
  merge: { label: "already in roster", cls: "bg-blue-50 text-blue-700 border-blue-200", why: "This property already exists in your SAVED roster (same unit + street). This person is added to that property — no duplicate is created. Usually means you've imported this property before." },
  update: { label: "updates in place", cls: "bg-violet-50 text-violet-700 border-violet-200", why: "This row carries an Ask Redline ID from your own export, so it EDITS that exact resident and their property — name, phone, email, unit, address. Nothing is duplicated, blank cells never erase saved data, and Undo puts the old values back." },
  id_unknown: { label: "ID not found", cls: "bg-slate-50 text-slate-600 border-slate-200", why: "This row has an Ask Redline ID we don't recognise (the resident may have been deleted), so it imports as a new person by the usual unit + street matching." },
  shares_unit: { label: "shares this property", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", why: `Another row in THIS file lists the same unit + street, so these ${V.lcs("person")} share one property. This is NOT an existing roster property — it's two people at the same address in your file.` },
  diff_street: { label: "same unit · diff street", cls: "bg-sky-50 text-sky-700 border-sky-200", why: "This unit number also appears on a different street. These are kept as SEPARATE properties — never merged together." },
  vehicle: { label: "vehicle", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", why: "A vehicle will be registered to this property." },
  extra_phone: { label: "2nd phone", cls: "bg-slate-50 text-slate-600 border-slate-200", why: "A second contact number will be saved on this person." },
  emergency: { label: "emergency", cls: "bg-rose-50 text-rose-600 border-rose-200", why: "An emergency contact will be saved on this property." },
  co_occupant: { label: "shares property", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", why: "Shares a property with the person listed just above (same unit + address)." },
});

const PAGE = 100;
const COLW_KEY = "askredline.importColW";
const DEFAULT_COLW = { add: 56, name: 200, unit: 108, address: 190, phone: 140, email: 230, flags: 160 };
const CARRIED_FLAGS = ["merge", "update", "id_unknown", "shares_unit", "diff_street", "co_occupant", "duplicate", "vehicle", "emergency", "extra_phone"];

// A resident we can actually reach: a plausible phone (>=10 digits) OR an email.
function contactable(r) {
  const digits = (r.phone || "").replace(/\D/g, "");
  return (!!r.phone && digits.length >= 10) || !!(r.email || "").trim();
}

// Live re-check of the flags a manager can fix by editing (phone / name / contactability). The
// rest are kept as the server computed them (they need whole-file / existing-roster context).
function recomputeRow(r) {
  const flags = (r.flags || []).filter((f) => CARRIED_FLAGS.includes(f));
  const digits = (r.phone || "").replace(/\D/g, "");
  if (r.phone && digits.length > 0 && digits.length < 10) flags.push("invalid_phone");
  if (!(r.name || "").trim() && !flags.includes("update")) flags.push("missing_name");
  // Round-trip rows (our own export, carrying an Ask Redline ID / Home ID) already exist — they're edits, not new contacts.
  if (!contactable(r) && !flags.includes("co_occupant") && !flags.includes("update")) flags.push("no_contact");
  const status = ["invalid_phone", "missing_name", "duplicate"].some((f) => flags.includes(f)) ? "warn" : "ok";
  return { ...r, flags, status };
}

/* Guided roster import: Upload → map columns & REVIEW/EDIT every row → import → offer invites.
   Nothing is written until the manager confirms on the review step; excluded rows never import. */
export function ImportWizard({ tenantId, open, onClose, onDone, texting = false, emailing = false, initialFile = null }) {
  const FLAG_META = flagMetaFactory();
  const [step, setStep] = useState("upload"); // upload | review | done
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [fields, setFields] = useState([]);
  const [samples, setSamples] = useState({});
  const [mapping, setMapping] = useState({});
  const [counts, setCounts] = useState(null);
  const [records, setRecords] = useState([]); // each row + `_include`
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("all");
  const [sortReviewFirst, setSortReviewFirst] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [colW, setColW] = useState(() => {
    try { return { ...DEFAULT_COLW, ...JSON.parse(localStorage.getItem(COLW_KEY) || "{}") }; }
    catch { return DEFAULT_COLW; }
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [splitCouples, setSplitCouples] = useState(true);
  const [invText, setInvText] = useState(true);
  const [invEmail, setInvEmail] = useState(false);
  const [invScope, setInvScope] = useState("phoneless");
  const [inviteMsg, setInviteMsg] = useState("");
  const msgRef = useRef(null);
  const insertPlaceholder = (token) => {
    const el = msgRef.current;
    const cur = inviteMsg || "";
    if (!el) { setInviteMsg(cur + token); return; }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? start;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setInviteMsg(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      try { el.setSelectionRange(pos, pos); } catch {}
    });
  };
  const [origMsg, setOrigMsg] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [origSubject, setOrigSubject] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [origReplyTo, setOrigReplyTo] = useState("");
  const [replyFallback, setReplyFallback] = useState("");
  const [saveDefault, setSaveDefault] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testSent, setTestSent] = useState("");
  const [inviteLogoPath, setInviteLogoPath] = useState("");
  const [siteLogoPath, setSiteLogoPath] = useState("");
  const [logoVer, setLogoVer] = useState(0);
  const [logoBusy, setLogoBusy] = useState(false);
  const [fromName, setFromName] = useState("");
  const [origFromName, setOrigFromName] = useState("");
  const [noReply, setNoReply] = useState(false);
  const [origNoReply, setOrigNoReply] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const [fullPreview, setFullPreview] = useState(null);
  const [fullBusy, setFullBusy] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewChannel, setPreviewChannel] = useState("text");
  const [sampleName, setSampleName] = useState("");
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setStep("upload"); setFile(null); setColumns([]); setFields([]); setSamples({}); setMapping({});
    setCounts(null); setRecords([]); setPage(0); setBusy(false); setResult(null); setSplitCouples(true);
    setInvText(texting); setInvEmail(!texting && emailing); setInvScope("phoneless");
    setSaveDefault(false);
    setPreviewChannel(texting ? "text" : "email"); setPreviewData(null); setSampleName("");
  }, [texting, emailing]);

  useEffect(() => { if (open) reset(); }, [open, reset]);

  useEffect(() => {
    if (!open || !tenantId) return;
    api.get(`/tenants/${tenantId}`).then((r) => {
      const m = r.data?.invite_message || "";
      setOrigMsg(m); setInviteMsg(nameSafe(m));
      const community = r.data?.name || "";
      const savedSubj = (r.data?.invite_email_subject || "").trim();
      setOrigSubject(savedSubj);
      setEmailSubject(savedSubj || `Welcome to ${community}, your ${V.lc("org")} concierge`);
      const rt = (r.data?.invite_reply_to || "").trim();
      setOrigReplyTo(rt); setReplyTo(rt);
      setReplyFallback((r.data?.public_site?.contact_email || r.data?.rcs_branding?.contact_email || r.data?.contact_email || "").trim());
      setInviteLogoPath((r.data?.invite_logo_path || "").trim());
      setSiteLogoPath(((r.data?.public_site || {}).logo_path || "").trim());
      setLogoVer(Date.now());
      setTenantName(r.data?.name || "");
      const fn = (r.data?.invite_from_name || "").trim();
      setOrigFromName(fn); setFromName(fn);
      const nr = !!r.data?.invite_reply_to_off;
      setOrigNoReply(nr); setNoReply(nr);
    }).catch(() => {});
  }, [open, tenantId]);

  useEffect(() => {
    if (step !== "done" || !tenantId || !(texting || emailing)) return;
    const t = setTimeout(() => {
      api.post(`/tenants/${tenantId}/households/invite-preview`,
        { custom_message: inviteMsg.trim(), email_subject: emailSubject.trim() })
        .then((r) => { setPreviewData(r.data || null); setSampleName(r.data?.sample_name || ""); })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [inviteMsg, emailSubject, step, tenantId, texting, emailing]);

  const runPreview = async (f, mp, split = true) => {
    const fd = new FormData();
    fd.append("file", f);
    if (mp) fd.append("mapping", JSON.stringify(mp));
    fd.append("split_couples", split ? "true" : "false");
    const r = await api.post(`/tenants/${tenantId}/households/import/preview`, fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    setColumns(r.data.columns || []);
    setFields(r.data.fields || []);
    setSamples(r.data.samples || {});
    setMapping(r.data.mapping || {});
    setCounts(r.data.counts || null);
    setRecords((r.data.records || []).map((rec) => {
      const nr = recomputeRow(rec);
      // Auto-skip un-contactable rows (no phone AND no email; co-occupants are reachable via their property).
      return { ...nr, _include: !nr.flags.includes("no_contact"), _userTouchedInclude: false };
    }));
    setPage(0);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await startWithFile(f);
  };

  // iter252/M4 — the roster's "Quick import" used to POST straight to /import/commit, writing
  // to the live roster with no review and no visible undo. It now hands the file to this
  // wizard, so every import goes through the same preview → review → commit path.
  const startWithFile = useCallback(async (f) => {
    setFile(f); setBusy(true);
    try { await runPreview(f, null, splitCouples); setStep("review"); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't read that file"); }
    finally { setBusy(false); }
  }, [runPreview, splitCouples]);

  const startedRef = useRef(null);
  useEffect(() => {
    if (open && initialFile && startedRef.current !== initialFile) {
      startedRef.current = initialFile;
      startWithFile(initialFile);
    }
    if (!open) startedRef.current = null;
  }, [open, initialFile, startWithFile]);

  // Re-validate (debounced) whenever the manager changes a column mapping.
  const mapRef = useRef(null);
  const changeMap = (field, col) => {
    const next = { ...mapping, [field]: col };
    setMapping(next);
    clearTimeout(mapRef.current);
    mapRef.current = setTimeout(() => { if (file) runPreview(file, next, splitCouples).catch(() => {}); }, 450);
  };

  const toggleSplit = (val) => {
    setSplitCouples(val);
    if (file) { setBusy(true); runPreview(file, mapping, val).catch(() => {}).finally(() => setBusy(false)); }
  };

  const updateField = (idx, field, value) =>
    setRecords((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const nr = recomputeRow({ ...r, [field]: value });
      // Follow contactability automatically UNLESS the manager set this row's checkbox themselves.
      if (!nr._userTouchedInclude) nr._include = !nr.flags.includes("no_contact");
      return nr;
    }));
  const toggleInclude = (idx) =>
    setRecords((prev) => prev.map((r, i) => (i === idx ? { ...r, _include: !r._include, _userTouchedInclude: true } : r)));
  const deleteRow = (idx) => setRecords((prev) => prev.filter((_, i) => i !== idx));
  const deleteExcluded = () => setRecords((prev) => prev.filter((r) => r._include));

  const included = useMemo(() => records.filter((r) => r._include), [records]);
  const allIncluded = records.length > 0 && included.length === records.length;
  const toggleAll = () => setRecords((prev) => prev.map((r) => ({ ...r, _include: !allIncluded, _userTouchedInclude: true })));
  const excludeWarnings = () =>
    setRecords((prev) => prev.map((r) => (r.status === "warn" ? { ...r, _include: false, _userTouchedInclude: true } : r)));

  const tally = useMemo(() => ({
    total: included.length,
    ok: included.filter((r) => r.status === "ok").length,
    warn: included.filter((r) => r.status === "warn").length,
    merge: included.filter((r) => (r.flags || []).includes("merge")).length,
    vehicles: included.filter((r) => (r.flags || []).includes("vehicle")).length,
    invalid_phone: included.filter((r) => (r.flags || []).includes("invalid_phone")).length,
    duplicate: included.filter((r) => (r.flags || []).includes("duplicate")).length,
  }), [included]);

  const matchesFilter = (r) => {
    if (filter === "all") return true;
    if (filter === "review") return r.status === "warn";
    if (filter === "excluded") return !r._include;
    return (r.flags || []).includes(filter);
  };
  const visibleRows = (() => {
    let rows = records.map((r, i) => ({ r, i })).filter(({ r }) => matchesFilter(r));
    if (sortCol) {
      rows = rows.slice().sort((a, b) => {
        const c = (a.r[sortCol] || "").toString().localeCompare((b.r[sortCol] || "").toString(), undefined, { numeric: true, sensitivity: "base" });
        return sortDir === "asc" ? c : -c;
      });
    } else if (sortReviewFirst) {
      rows = rows.slice().sort((a, b) => (b.r.status === "warn") - (a.r.status === "warn"));
    }
    return rows;
  })();
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE));
  const curPage = Math.min(page, pageCount - 1);
  const pageRows = visibleRows.slice(curPage * PAGE, curPage * PAGE + PAGE);
  const filterDefs = [
    { id: "all", label: "All rows", n: records.length },
    { id: "review", label: "To review", n: records.filter((r) => r.status === "warn").length },
    { id: "no_contact", label: "No contact", n: records.filter((r) => (r.flags || []).includes("no_contact")).length },
    { id: "merge", label: "Already in roster", n: records.filter((r) => (r.flags || []).includes("merge")).length },
    { id: "update", label: "Updates in place", n: records.filter((r) => (r.flags || []).includes("update")).length },
    { id: "shares_unit", label: "Shares a property", n: records.filter((r) => (r.flags || []).includes("shares_unit")).length },
    { id: "diff_street", label: "Same unit · diff street", n: records.filter((r) => (r.flags || []).includes("diff_street")).length },
    { id: "vehicle", label: "Vehicles", n: records.filter((r) => (r.flags || []).includes("vehicle")).length },
    { id: "excluded", label: "Excluded", n: records.filter((r) => !r._include).length },
  ].filter((f) => f.id === "all" || f.n > 0);
  const applyFilter = (id) => { setFilter(id); setPage(0); };
  const noContactCount = records.filter((r) => (r.flags || []).includes("no_contact")).length;
  const excludedCount = records.filter((r) => !r._include).length;
  const toggleSort = (field) => {
    setSortReviewFirst(false);
    setPage(0);
    if (sortCol !== field) { setSortCol(field); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortCol(null); setSortDir("asc"); }
  };
  const sortTh = (field, label) => (
    <th className="relative text-left px-2 py-2 font-semibold">
      <button type="button" data-testid={`import-sort-col-${field}`} onClick={() => toggleSort(field)}
        className="inline-flex items-center gap-1 hover:text-ink transition-colors">
        {label}
        {sortCol === field
          ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3 text-ink" /> : <ChevronDown className="h-3 w-3 text-ink" />)
          : <ChevronsUpDown className="h-3 w-3 opacity-30" />}
      </button>
      {rHandle(field)}
    </th>
  );

  // Drag-to-resize column widths (persisted). Double-click a handle resets that column.
  useEffect(() => { try { localStorage.setItem(COLW_KEY, JSON.stringify(colW)); } catch { /* ignore */ } }, [colW]);
  const startResize = (key, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colW[key] || 120;
    const onMove = (ev) => setColW((p) => ({ ...p, [key]: Math.max(56, startW + (ev.clientX - startX)) }));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const resetCol = (key) => setColW((p) => ({ ...p, [key]: DEFAULT_COLW[key] }));
  const rHandle = (key) => (
    <span onMouseDown={(e) => startResize(key, e)} onDoubleClick={() => resetCol(key)}
      data-testid={`import-col-resize-${key}`} title="Drag to resize · double-click to reset"
      className="group absolute top-0 right-0 z-20 flex h-full w-3.5 -mr-1.5 justify-center cursor-col-resize select-none">
      <span className="my-0.5 w-[2px] rounded-full bg-sand-300 group-hover:bg-redline-500 group-hover:w-[3px] transition-all" />
      <span className="pointer-events-none absolute top-0 h-2 w-2 rounded-b-sm bg-sand-400 group-hover:bg-redline-500 transition-colors" />
    </span>
  );
  const tableW = DEFAULT_COLW && (colW.add + colW.name + colW.unit + colW.address + colW.phone + colW.email + colW.flags);


  const commit = async () => {
    if (!included.length) return;
    setBusy(true);
    try {
      const payload = {
        records: included.map(({ _include, _userTouchedInclude, ...rest }) => rest),
        filename: file?.name || "",
      };
      const r = await api.post(`/tenants/${tenantId}/households/import/commit-rows`, payload);
      setResult(r.data);
      setStep("done");
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const undoImport = async () => {
    if (!result?.batch_id) return;
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/households/import/undo`, { batch_id: result.batch_id });
      toast.success(`Undone — removed ${r.data.removed_people} ${V.lcs("person")} · ${r.data.removed_households} properties`);
      onDone?.(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't undo import"); }
    finally { setBusy(false); }
  };

  // Swap a baked-in literal first name back to {name} inside a greeting, so a saved default
  // greets every resident by their OWN name — never "Hi Dawson" for all. Works even when the
  // baked-in name isn't the current sample resident (a common leftover from earlier sends).
  const _escRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const _GREET_STOP = new Set(["there", "all", "everyone", "everybody", "neighbor", "neighbors",
    "neighbour", "neighbours", "resident", "residents", "folks", "team", "friend", "friends",
    "homeowner", "homeowners", "member", "members", "owner", "owners", "yall", "again", "welcome", "name"]);
  const nameSafe = (msg) => {
    if (!msg) return msg;
    // A greeting immediately followed by a single first-name token → {name} (skip common words).
    let out = msg.replace(/\b(hi|hello|hey|dear|hiya|greetings)([ ,]+)([A-Za-z][A-Za-z'’-]*)\b/gi,
      (m, g, sep, tok) => _GREET_STOP.has(tok.toLowerCase()) ? m : `${g}${sep}{name}`);
    // Also catch the exact sample resident's name at the very start of the message.
    const n = (sampleName || "").trim();
    if (n) out = out.replace(new RegExp(`^${_escRx(n)}\\b`), "{name}");
    return out;
  };

  const logoUrl = (inviteLogoPath || siteLogoPath)
    ? `${API}/public/tenant/${tenantId}/logo?use=invite&v=${logoVer}`
    : "";
  const uploadInviteLogo = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Keep the logo under 4 MB"); return; }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, "logo.png");
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/tenants/${tenantId}/config`, { invite_logo_path: up.data.path });
      setInviteLogoPath(up.data.path); setLogoVer(Date.now());
      toast.success("Logo updated for invite emails");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't upload that logo"); }
    finally { setLogoBusy(false); }
  };
  const removeInviteLogo = async () => {
    setLogoBusy(true);
    try {
      await api.put(`/tenants/${tenantId}/config`, { invite_logo_path: "" });
      setInviteLogoPath(""); setLogoVer(Date.now());
      toast.success(siteLogoPath ? "Removed — using your Public Page logo" : "Logo removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove the logo"); }
    finally { setLogoBusy(false); }
  };

  const openFullPreview = async () => {
    setFullBusy(true); setFullPreview({});
    try {
      const r = await api.post(`/tenants/${tenantId}/households/invite-email-preview`, {
        custom_message: nameSafe(inviteMsg.trim()),
        email_subject: emailSubject.trim(),
        invite_from_name: fromName.trim(),
        invite_reply_to: replyTo.trim(),
        invite_reply_to_off: noReply,
      });
      setFullPreview(r.data);
    } catch { toast.error("Couldn't load the full preview"); setFullPreview(null); }
    finally { setFullBusy(false); }
  };

  const _streetBase = (a) => (a || "").toLowerCase().replace(/[.,]/g, "").replace(/\s+/g, " ").trim()
    .replace(/\s(st|street|ave|avenue|av|rd|road|dr|drive|blvd|boulevard|ln|lane|ct|court|pl|place|cir|circle|way|ter|terrace|pkwy|hwy|trl|trail|sq|square)$/, "").trim();
  const homeKey = (r) => `${(r.unit || "").trim().toLowerCase()}|${_streetBase(r.address)}`;
  const homeCounts = useMemo(() => {
    const m = {};
    for (const r of records) { const k = homeKey(r); if (k.replace("|", "")) m[k] = (m[k] || 0) + 1; }
    return m;
  }, [records]);
  const showHead = (r) => (homeCounts[homeKey(r)] || 0) > 1 || (r.flags || []).includes("merge") || (r.flags || []).includes("shares_unit") || (r.flags || []).includes("co_occupant");
  const setHead = (idx) => {
    const key = homeKey(records[idx]);
    setRecords((prev) => prev.map((r, j) => j === idx ? { ...r, is_head: true } : (homeKey(r) === key ? { ...r, is_head: false } : r)));
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const val = testTo.trim();
      const channel = val ? (val.includes("@") ? "email" : "text") : (previewChannel === "email" && emailing ? "email" : "text");
      const custom = nameSafe(inviteMsg.trim());
      const payload = { channel };
      if (val) payload.to = val;
      if (custom) payload.custom_message = custom;
      if (channel === "email") {
        const subj = emailSubject.trim();
        if (subj) payload.email_subject = subj;
        payload.invite_reply_to = replyTo.trim();
        const fn = fromName.trim();
        if (fn) payload.invite_from_name = fn;
        payload.invite_reply_to_off = noReply;
      }
      const r = await api.post(`/tenants/${tenantId}/households/invite-test`, payload);
      setTestSent(r.data?.to || "you");
      toast.success(`Test ${r.data?.channel === "email" ? "email" : "text"} sent to ${r.data?.to || "you"}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't send the test"); }
    finally { setTestBusy(false); }
  };

  const sendInvites = async () => {
    const channels = [];
    if (invText && texting) channels.push("text");
    if (invEmail && emailing) channels.push("email");
    if (!channels.length) { onClose(); return; }
    setBusy(true);
    try {
      const custom = nameSafe(inviteMsg.trim());
      const changed = !!custom && custom !== (origMsg || "").trim();
      const subj = emailSubject.trim();
      const emailOn = channels.includes("email");
      const subjChanged = emailOn && !!subj && subj !== (origSubject || "").trim();
      const payload = { channels, email_scope: invScope, rate_per_hour: 250, quiet_hours: true };
      if (changed) payload.custom_message = custom;
      if (emailOn && subj) payload.email_subject = subj;
      const rt = replyTo.trim();
      if (emailOn && rt !== (origReplyTo || "").trim()) payload.invite_reply_to = rt;
      const fn = fromName.trim();
      if (emailOn && fn !== (origFromName || "").trim()) payload.invite_from_name = fn;
      if (emailOn && noReply !== origNoReply) payload.invite_reply_to_off = noReply;
      if (saveDefault && (changed || subjChanged)) payload.save_default = true;
      const r = await api.post(`/tenants/${tenantId}/households/invite-campaign`, payload);
      toast.success(`Launch started — inviting ${r.data.total} ${V.lc("person")}${r.data.total === 1 ? "" : "s"} in the background`, {
        description: "Track progress on the Roster page or your dashboard.",
      });
      onDone?.(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start the invites"); }
    finally { setBusy(false); }
  };

  const PRIMARY_FIELDS = ["name", "address", "unit", "phone", "email"];
  const mapRow = (f) => (
    <label key={f.field} className="flex items-center justify-between gap-2 text-sm">
      <span className="text-ink/70 shrink-0">{f.label}</span>
      <select data-testid={`import-map-${f.field}`} value={mapping[f.field] || ""}
        onChange={(e) => changeMap(f.field, e.target.value)}
        className="min-w-0 flex-1 max-w-[55%] rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
        <option value="">— skip —</option>
        {columns.map((c) => <option key={c} value={c}>{samples[c] ? `${c} — e.g. ${samples[c]}` : c}</option>)}
      </select>
    </label>
  );

  const cell = "px-2 py-1.5";
  const inputCls = "w-full rounded-md border border-transparent hover:border-sand-200 focus:border-redline-400 bg-transparent focus:bg-white px-1.5 py-1 text-sm outline-none focus:ring-1 focus:ring-redline-500/20";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={step === "review" ? "max-w-6xl w-[96vw]" : "max-w-2xl"} data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-redline-500" /> Import {V.lcs("person")}
          </DialogTitle>
          <DialogDescription>
            {step === "upload" && "Upload a CSV or Excel file. Any column names are fine, we'll map them for you."}
            {step === "review" && "Match your columns, then review and edit every row. Nothing imports until you confirm."}
            {step === "done" && "Import complete."}
          </DialogDescription>
        </DialogHeader>

        {/* Step: upload */}
        {step === "upload" && (
          <div data-testid="import-step-upload" className="py-4">
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="w-full rounded-2xl border-2 border-dashed border-sand-300 hover:border-redline-400 bg-sand-50 py-12 flex flex-col items-center gap-3 transition-colors disabled:opacity-60">
              <UploadCloud className="h-10 w-10 text-redline-400" strokeWidth={1.5} />
              <span className="font-semibold text-ink">{busy ? "Reading…" : "Choose a CSV or Excel file"}</span>
              <span className="text-xs text-ink/50">Name, address, unit, phone, email — in any order</span>
            </button>
            <input ref={fileRef} data-testid="import-wizard-file" type="file" accept=".csv,.xlsx,.xls" hidden onChange={onFile} />
          </div>
        )}

        {/* Step: map + review/edit */}
        {step === "review" && (
          <div data-testid="import-step-review" className="space-y-4">
            <label className="flex items-start gap-2.5 text-sm cursor-pointer bg-sand-50 border border-sand-200 rounded-xl px-3 py-2.5">
              <input type="checkbox" data-testid="import-split-couples" checked={splitCouples} onChange={(e) => toggleSplit(e.target.checked)} className="h-4 w-4 accent-ink mt-0.5" />
              <span className="text-ink/80">Split two names in one cell (e.g. <b>&quot;Alex &amp; Sam Lee&quot;</b>) into separate {V.lcs("person")} sharing the same home</span>
            </label>

            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-2">Match your columns to ours</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2">
                {fields.filter((f) => PRIMARY_FIELDS.includes(f.field)).map(mapRow)}
              </div>
              {fields.some((f) => !PRIMARY_FIELDS.includes(f.field)) && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-xs font-semibold text-ink/55 hover:text-ink select-none">Advanced fields (owner/tenant, property type, city/state/ZIP, notes, second phone, emergency contact, vehicle…)</summary>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2 mt-2">
                    {fields.filter((f) => !PRIMARY_FIELDS.includes(f.field)).map(mapRow)}
                  </div>
                </details>
              )}
            </div>

            <div className="flex items-start gap-2 rounded-xl bg-sky-50/70 border border-sky-100 px-3 py-2 text-[12px] text-ink/70">
              <Scissors className="h-3.5 w-3.5 text-sky-600 mt-0.5 shrink-0" />
              <span>If your file crams the whole street address into one “Unit” cell (like <b>“100 Oak St #5”</b>), we split it into <b>Address</b> + <b>Unit</b> automatically. Homes are matched by <b>unit&nbsp;+&nbsp;street together</b>, so the same unit number on different streets stays separate.</span>
            </div>

            {/* One consistent outcome line — the filter chips below break down the same file. */}
            <p data-testid="import-summary" className="text-sm text-ink/75">
              <b className="text-ink">{records.length}</b> row{records.length === 1 ? "" : "s"} in your file — <b className="text-emerald-700">{included.length}</b> will import{excludedCount > 0 && <>, <b className="text-ink/60">{excludedCount}</b> skipped (unchecked)</>}. Tap a chip below to review each group.
            </p>

            {noContactCount > 0 && (
              <p data-testid="import-no-contact-note" className="flex items-start gap-2 text-xs text-orange-800 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                <PhoneOff className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span><b>{noContactCount}</b> row{noContactCount === 1 ? "" : "s"} have no phone or email, so {noContactCount === 1 ? "it's" : "they're"} <b>skipped by default</b> (you can't message them). Add a phone or email to a row and it re-includes itself, or use the <b>No contact</b> filter to review them.</span>
              </p>
            )}

            {counts?.cross_street_units > 0 && (
              <p data-testid="import-cross-street-note" className="flex items-start gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span><b>{counts.cross_street_units}</b> unit number{counts.cross_street_units === 1 ? "" : "s"} appear on more than one street. These are kept as <b>separate homes</b> (they are no longer merged together).</span>
              </p>
            )}

            {/* Editable, paginated review table */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Review &amp; edit every row</p>
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="import-sort-review" onClick={() => { setSortReviewFirst((v) => !v); setSortCol(null); setPage(0); }}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold transition-colors ${sortReviewFirst ? "text-ink" : "text-ink/50 hover:text-ink"}`}
                    title="Bring the rows that need review to the top (keeps the whole list)">
                    <ArrowDownUp className="h-3.5 w-3.5" /> Review first{sortReviewFirst ? " ✓" : ""}
                  </button>
                  {excludedCount > 0 && (
                    <button type="button" data-testid="import-delete-excluded" onClick={deleteExcluded}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600/80 hover:text-red-700 transition-colors" title="Permanently remove every unchecked row from this worksheet">
                      <Trash2 className="h-3.5 w-3.5" /> Delete {excludedCount} skipped
                    </button>
                  )}
                  {tally.warn > 0 && (
                    <button type="button" data-testid="import-exclude-warnings" onClick={excludeWarnings}
                      className="text-[11px] font-semibold text-amber-700 hover:underline">Skip all {tally.warn} flagged</button>
                  )}
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-ink/60 cursor-pointer">
                    <input type="checkbox" data-testid="import-select-all" checked={allIncluded} onChange={toggleAll} className="h-3.5 w-3.5 accent-ink" />
                    Include all
                  </label>
                </div>
              </div>
              <div data-testid="import-filter-bar" className="flex flex-wrap items-center gap-1.5 mb-2">
                {filterDefs.map((f) => (
                  <button key={f.id} type="button" data-testid={`import-filter-${f.id}`} onClick={() => applyFilter(f.id)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${filter === f.id ? "bg-ink text-white border-ink" : "bg-white text-ink/60 border-sand-200 hover:border-ink/40"}`}>
                    {f.label} <span className={filter === f.id ? "text-white/70" : "text-ink/40"}>{f.n}</span>
                  </button>
                ))}
              </div>
              <div className="rounded-xl border border-sand-200 overflow-hidden">
                <div className="max-h-[46vh] overflow-auto">
                  <table className="text-sm table-fixed" style={{ width: tableW }}>
                    <colgroup>
                      <col style={{ width: colW.add }} />
                      <col style={{ width: colW.name }} />
                      <col style={{ width: colW.address }} />
                      <col style={{ width: colW.unit }} />
                      <col style={{ width: colW.phone }} />
                      <col style={{ width: colW.email }} />
                      <col style={{ width: colW.flags }} />
                    </colgroup>
                    <thead className="bg-sand-50 text-ink/50 text-[11px] uppercase tracking-wide sticky top-0 z-10">
                      <tr>
                        <th className="text-center px-2 py-2 font-semibold">Add</th>
                        {sortTh("name", "Name")}
                        {sortTh("address", "Address")}
                        {sortTh("unit", "Unit")}
                        {sortTh("phone", "Phone")}
                        {sortTh("email", "Email")}
                        <th className="relative text-left px-2 py-2 font-semibold">Flags{rHandle("flags")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sand-100">
                      {pageRows.map(({ r, i }) => (
                        <tr key={i} data-testid={`import-review-row-${i}`}
                          className={!r._include ? "bg-sand-100/70" : r.status === "warn" ? "bg-amber-50/40" : ""}>
                          <td className={`${cell}`}>
                            <div className="flex items-center justify-center gap-1.5">
                              <input type="checkbox" data-testid={`import-row-include-${i}`} checked={r._include}
                                onChange={() => toggleInclude(i)} className="h-4 w-4 accent-ink" />
                              <button type="button" data-testid={`import-row-delete-${i}`} onClick={() => deleteRow(i)}
                                title="Remove this row from the import" className="text-ink/25 hover:text-red-600 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className={cell}>
                            <div className="flex items-center gap-1">
                              <input data-testid={`import-edit-name-${i}`} value={r.name || ""}
                                onChange={(e) => updateField(i, "name", e.target.value)} className={`flex-1 min-w-0 ${inputCls} ${r._include ? "" : "text-ink/40"}`} placeholder="—" />
                              {showHead(r) && (
                                <button type="button" data-testid={`import-row-head-${i}`} onClick={() => setHead(i)}
                                  title={r.is_head ? `Head of ${V.lc("household")} — this person leads the property` : `Make head of ${V.lc("household")} — this person will lead the property`}
                                  className={`shrink-0 rounded-full p-1 transition-colors ${r.is_head ? "text-amber-500" : "text-ink/25 hover:text-amber-500"}`}>
                                  <Crown className="h-4 w-4" fill={r.is_head ? "currentColor" : "none"} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className={cell}>
                            <input data-testid={`import-edit-address-${i}`} value={r.address || ""}
                              onChange={(e) => updateField(i, "address", e.target.value)} className={`${inputCls} ${r._include ? "" : "text-ink/40"}`} placeholder="—" />
                          </td>
                          <td className={cell}>
                            <input data-testid={`import-edit-unit-${i}`} value={r.unit || ""}
                              onChange={(e) => updateField(i, "unit", e.target.value)} className={`${inputCls} ${r._include ? "" : "text-ink/40"}`} placeholder="—" />
                          </td>
                          <td className={cell}>
                            <input data-testid={`import-edit-phone-${i}`} value={r.phone || ""}
                              onChange={(e) => updateField(i, "phone", e.target.value)} className={`${inputCls} ${r._include ? "" : "text-ink/40"}`} placeholder="—" />
                          </td>
                          <td className={cell}>
                            <input data-testid={`import-edit-email-${i}`} value={r.email || ""}
                              onChange={(e) => updateField(i, "email", e.target.value)} className={`${inputCls} ${r._include ? "" : "text-ink/40"}`} placeholder="—" />
                          </td>
                          <td className={cell}>
                            <span className="flex flex-wrap gap-1">
                              {(r.flags || []).map((fl) => (
                                <span key={fl} title={FLAG_META[fl]?.why || FLAG_META[fl]?.label || fl} className={`cursor-help text-[10px] font-semibold rounded-full border px-1.5 py-0.5 ${FLAG_META[fl]?.cls || "bg-sand-100 text-ink/60 border-sand-200"}`}>{FLAG_META[fl]?.label || fl}</span>
                              ))}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {!records.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-ink/40">No rows found in this file.</td></tr>}
                      {records.length > 0 && !visibleRows.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-ink/40">No rows match this filter.</td></tr>}
                    </tbody>
                  </table>
                </div>
                {pageCount > 1 && (
                  <div className="flex items-center justify-between px-3 py-2 border-t border-sand-100 bg-sand-50/60 text-xs">
                    <button type="button" data-testid="import-review-prev-page" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={curPage === 0}
                      className="inline-flex items-center gap-1 font-semibold text-ink/70 hover:text-ink disabled:opacity-30"><ChevronLeft className="h-4 w-4" /> Prev</button>
                    <span data-testid="import-review-page-info" className="text-ink/55">
                      Rows <b className="text-ink/70">{curPage * PAGE + 1}–{Math.min((curPage + 1) * PAGE, visibleRows.length)}</b> of {visibleRows.length} · page {curPage + 1}/{pageCount}
                    </span>
                    <button type="button" data-testid="import-review-next-page" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={curPage >= pageCount - 1}
                      className="inline-flex items-center gap-1 font-semibold text-ink/70 hover:text-ink disabled:opacity-30">Next <ChevronRight className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-ink/45 mt-1.5">Click any cell to fix it. Uncheck a row to skip it (only checked rows import), or use the trash icon to remove it from the list.{records.some(showHead) && <> Where two people share a home, tap the <Crown className="inline h-3 w-3 text-amber-500 -mt-0.5" /> crown to pick who leads that property.</>} Drag a column&rsquo;s right edge to resize it (double-click the edge to reset).</p>
            </div>

            <div className="flex justify-between pt-3 border-t border-sand-100">
              <button data-testid="import-back" onClick={() => setStep("upload")}
                className="rounded-full px-5 py-2.5 text-sm font-semibold border border-sand-200 hover:border-ink transition-colors inline-flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button data-testid="import-commit" onClick={commit} disabled={busy || !included.length}
                className="rounded-full bg-redline-500 text-white px-6 py-2.5 text-sm font-semibold hover:bg-redline-600 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy ? "Importing…" : <>Import {tally.total} {V.lc("person")}{tally.total === 1 ? "" : "s"} <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </div>
        )}

        {/* Step: done + invite */}
        {step === "done" && result && (
          <div data-testid="import-step-done" className="space-y-4 py-1">
            {/* Header: success summary + Undo at top-right (a back/undo control belongs up here) */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                  <Check className="h-5 w-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading font-extrabold text-base text-ink leading-tight">{!result.new && !result.merged && result.updated ? `Updated ${result.updated} ${V.lcs("person")} in place` : `Imported ${result.imported} ${V.lcs("person")}`}</h3>
                  <p className="text-xs text-ink/60 mt-0.5">{result.new} new propert{result.new === 1 ? "y" : "ies"}{result.updated ? ` · ${result.updated} updated in place` : ""}{result.merged_existing ? ` · ${result.merged_existing} added to an existing home` : ""}{result.shares_unit ? ` · ${result.shares_unit} sharing a home with another row` : ""}{!result.merged_existing && !result.shares_unit && result.merged ? ` · ${result.merged} added to existing` : ""}{result.vehicles ? ` · ${result.vehicles} vehicles` : ""}</p>
                </div>
              </div>
              <button data-testid="import-undo" onClick={undoImport} disabled={busy}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-ink/70 hover:text-red-600 transition-colors disabled:opacity-50"
                title="Remove everything this import just added">
                <Undo2 className="h-3.5 w-3.5" /> Undo import
              </button>
            </div>

            {(texting || emailing) ? (
              <div className="rounded-2xl border border-sand-200 overflow-hidden">
                {/* 1 — WHO */}
                <div className="p-4 border-b border-sand-100 bg-sand-50/50 space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Who should we invite?</p>
                  {texting && (
                    <label data-testid="import-invite-text-row"
                      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${invText ? "border-ink/30 bg-white" : "border-sand-200 bg-white/50"}`}>
                      <MessageSquarePlus className="h-4 w-4 text-ink/50 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-ink">Text message</span>
                        <span className="block text-[11px] text-ink/50">{V.many("person")} with a mobile number on file</span>
                      </span>
                      <input type="checkbox" data-testid="import-invite-text" checked={invText} onChange={(e) => setInvText(e.target.checked)} className="h-4 w-4 accent-ink" />
                    </label>
                  )}
                  {emailing && (
                    <label data-testid="import-invite-email-row"
                      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${invEmail ? "border-ink/30 bg-white" : "border-sand-200 bg-white/50"}`}>
                      <Mail className="h-4 w-4 text-ink/50 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-ink">Email</span>
                        <span className="block text-[11px] text-ink/50">Reach {V.lcs("person")} by email</span>
                      </span>
                      <input type="checkbox" data-testid="import-invite-email" checked={invEmail} onChange={(e) => setInvEmail(e.target.checked)} className="h-4 w-4 accent-ink" />
                    </label>
                  )}
                  {invEmail && emailing && (
                    <div data-testid="import-invite-scope" className="ml-8 flex items-center gap-0.5 rounded-full bg-white border border-sand-200 p-0.5 w-fit">
                      <button type="button" data-testid="import-scope-phoneless" onClick={() => setInvScope("phoneless")}
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${invScope === "phoneless" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>Only those with no phone</button>
                      <button type="button" data-testid="import-scope-all" onClick={() => setInvScope("all")}
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${invScope === "all" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>Everyone with an email</button>
                    </div>
                  )}
                </div>

                {/* 2 — WHAT they'll get (edit + live preview) */}
                <div className="p-4 space-y-3">
                  {invEmail && emailing && (
                    <div>
                      <label htmlFor="invite-subject" className="text-xs font-semibold text-ink/70">Email subject</label>
                      <input id="invite-subject" data-testid="import-invite-subject" value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder={`Welcome to your ${V.lc("org")} concierge`}
                        className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                      <p className="text-[11px] text-ink/45 mt-1">{`{community}`} is filled in automatically. Shown only on emails.</p>
                    </div>
                  )}
                  {invEmail && emailing && (
                    <div>
                      <label htmlFor="invite-from-name" className="text-xs font-semibold text-ink/70">From name (sender)</label>
                      <input id="invite-from-name" data-testid="import-invite-from-name" value={fromName}
                        onChange={(e) => setFromName(e.target.value)}
                        placeholder={tenantName || `Your ${V.lc("org")}`}
                        className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                      <p className="text-[11px] text-ink/45 mt-1">Shows as the sender name (e.g. “{fromName.trim() || tenantName || `Your ${V.lc("org")}`}”). The address stays <span className="whitespace-nowrap">no-reply@askredline.com</span> (verified domain). Saved for future invites.</p>
                    </div>
                  )}
                  {invEmail && emailing && (
                    <div>
                      <label className="text-xs font-semibold text-ink/70">Invite email logo</label>
                      <div className="mt-1.5 flex items-center gap-3">
                        <div className="h-14 w-16 rounded-lg border border-sand-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                          {logoUrl ? (
                            <img data-testid="import-invite-logo-img" src={logoUrl} alt="Invite logo" className="h-full w-full object-contain" />
                          ) : (
                            <ImageIcon className="h-5 w-5 text-ink/30" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <label data-testid="import-invite-logo-upload" className={`cursor-pointer rounded-full border border-sand-200 bg-white px-3 py-1.5 text-xs font-bold text-ink hover:border-ink transition-colors inline-flex items-center gap-1.5 ${logoBusy ? "opacity-50 pointer-events-none" : ""}`}>
                              <UploadCloud className="h-3.5 w-3.5" /> {logoBusy ? "Uploading…" : (inviteLogoPath ? "Replace" : "Upload logo")}
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); e.target.value = ""; }} />
                            </label>
                            {inviteLogoPath && (
                              <button type="button" data-testid="import-invite-logo-remove" onClick={removeInviteLogo} disabled={logoBusy} className="text-xs font-bold text-ink/50 hover:text-red-600">Remove</button>
                            )}
                          </div>
                          <p className="text-[11px] text-ink/45 mt-1">
                            {inviteLogoPath ? "Shown at the top of the invite email." : siteLogoPath ? "Using your Public Page logo — upload one to override it." : `No logo — the email shows your ${V.lc("org")} name.`} Saved for future invites too.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {invEmail && emailing && (
                    <div>
                      <label htmlFor="invite-reply-to" className="text-xs font-semibold text-ink/70">Replies go to <span className="font-normal text-ink/40">— one address, or several separated by commas</span></label>
                      <input id="invite-reply-to" data-testid="import-invite-reply-to" type="text" value={replyTo}
                        onChange={(e) => setReplyTo(e.target.value)} disabled={noReply}
                        placeholder={replyFallback || `office@yourhoa.com, ${V.grp()}@yourhoa.com`}
                        className={`mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 ${noReply ? "opacity-50 bg-sand-50 cursor-not-allowed" : ""}`} />
                      <label className="flex items-center gap-2 mt-2 text-[11px] font-semibold text-ink/70 cursor-pointer">
                        <input type="checkbox" data-testid="import-invite-no-reply" checked={noReply} onChange={(e) => setNoReply(e.target.checked)} className="h-3.5 w-3.5 accent-ink" />
                        Don’t accept replies (send as no-reply)
                      </label>
                      <p className="text-[11px] text-ink/45 mt-1">
                        {noReply
                          ? `Replies go to an unmonitored no-reply address — ${V.lcs("person")} can’t reply.`
                          : replyTo.trim()
                            ? (replyTo.includes(",")
                                ? `${V.one("person")} replies go to all of these inboxes (e.g. your manager and ${V.a("board")}).`
                                : `${V.one("person")} replies land in this inbox.`)
                            : replyFallback
                              ? `Blank → replies currently go to ${replyFallback}.`
                              : `Not set yet — add an address so ${V.lc("person")} replies reach your office (otherwise they hit an unmonitored inbox).`}
                        {" "}The email sends from your {V.lc("org")} on askredline.com. Saved for future invites too.
                      </p>
                    </div>
                  )}
                  <div>
                    <label htmlFor="invite-message" className="text-xs font-semibold text-ink/70">Invite message {invText && invEmail ? "(used for both text and email)" : ""}</label>
                    <textarea ref={msgRef} id="invite-message" data-testid="import-invite-message" value={inviteMsg} onChange={(e) => setInviteMsg(e.target.value)}
                      rows={4} placeholder={`Leave blank to use your ${V.lc("org")}'s standard welcome message.`}
                      className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />
                    <SegmentMeter text={inviteMsg} extraChars={90} testId="import-segment-meter" />
                    <p className="text-[11px] text-ink/45 mt-1.5 font-medium">Personalization tags — these fill in automatically for each {V.lc("person")}. Tap one to insert it at your cursor:</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="import-placeholder-legend">
                      {[
                        ["{name}", "their first name"],
                        ["{community}", `your ${V.lc("org")} name`],
                        ["{persona}", "your concierge name"],
                        ["{enrollment_link}", "opt-in sign-up page"],
                      ].map(([tok, desc]) => (
                        <button type="button" key={tok} data-testid={`import-placeholder-${tok.replace(/[{}]/g, "")}`}
                          onClick={() => insertPlaceholder(tok)} title={`Insert ${tok}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-2.5 py-1 text-[11px] hover:border-ink hover:bg-sand-50 transition-colors">
                          <code className="font-bold text-ink">{tok}</code>
                          <span className="text-ink/45">{desc}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-ink/45 mt-1.5">
                      Add <code className="bg-sand-100 px-1 rounded">{"{enrollment_link}"}</code> and {V.lcs("person")} can share your {V.lc("org")}'s sign-up page so a tenant or roommate in their unit enrolls themselves. Texts always include the required opt-out notice (msg rates, STOP, HELP).
                    </p>
                    {nameSafe(inviteMsg) !== inviteMsg && (
                      <div className="mt-1.5 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span className="flex-1">This greets <span className="font-semibold">everyone</span> as “{sampleName}”. Use <code className="bg-amber-100 px-1 rounded">{"{name}"}</code> so each {V.lc("person")} sees their own first name.</span>
                        <button type="button" data-testid="import-invite-fix-name"
                          onClick={() => { setInviteMsg(nameSafe(inviteMsg)); setSaveDefault(true); toast.success(`Updated to use {name} — each ${V.lc("person")} is greeted by their own name`); }}
                          className="shrink-0 font-bold underline hover:no-underline">Fix</button>
                      </div>
                    )}
                  </div>

                  {previewData && (
                    <div data-testid="import-invite-preview" className="rounded-xl bg-sand-50 border border-sand-200 p-3">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Live preview</div>
                        {texting && emailing && (
                          <div className="flex items-center gap-0.5 rounded-full bg-white border border-sand-200 p-0.5 shrink-0">
                            <button type="button" data-testid="preview-tab-text" onClick={() => setPreviewChannel("text")}
                              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${previewChannel === "text" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>Text</button>
                            <button type="button" data-testid="preview-tab-email" onClick={() => setPreviewChannel("email")}
                              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${previewChannel === "email" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>Email</button>
                          </div>
                        )}
                      </div>
                      {previewChannel === "email" && emailing ? (
                        <div data-testid="preview-email" className="rounded-lg bg-white border border-sand-200 overflow-hidden shadow-sm">
                          {logoUrl && (
                            <div className="px-3 pt-3 pb-1 border-b border-sand-50">
                              <img src={logoUrl} alt="" data-testid="preview-email-logo" className="h-8 object-contain" />
                            </div>
                          )}
                          <div className="border-b border-sand-100 px-3 py-2">
                            <div className="text-[10px] text-ink/40 uppercase tracking-wide">From</div>
                            <div className="text-[13px] font-semibold text-ink">{fromName.trim() || tenantName || `Your ${V.lc("org")}`} <span className="font-normal text-ink/40">&lt;no-reply@askredline.com&gt;</span></div>
                            <div data-testid="preview-email-replyto" className="text-[10px] text-ink/40 mt-0.5">Reply-to: {noReply ? "none (no-reply)" : (replyTo.trim() || replyFallback || "none")}</div>
                          </div>
                          <div className="border-b border-sand-100 px-3 py-2">
                            <div className="text-[10px] text-ink/40 uppercase tracking-wide">Subject</div>
                            <div className="text-[13px] font-semibold text-ink">{previewData.email?.subject}</div>
                          </div>
                          <div className="px-3 py-2.5 text-[13px] text-ink whitespace-pre-wrap">{previewData.email?.body}</div>
                          {previewData.phone && (
                            <div data-testid="preview-email-phone" className="mx-3 mb-2 rounded-lg bg-sand-50 border border-sand-200 border-l-4 border-l-redline-500 px-3 py-2">
                              <div className="text-[9px] font-bold uppercase tracking-wide text-redline-600">Text or call your concierge</div>
                              <div className="text-[15px] font-extrabold text-ink leading-tight mt-0.5">{previewData.phone}</div>
                              <div className="text-[10px] text-ink/50">Save this number — answers 24/7 by text or call.</div>
                            </div>
                          )}
                          <div className="px-3 pb-3">
                            <span className="inline-block rounded-full bg-ink text-white text-[11px] font-semibold px-3 py-1.5">Get started →</span>
                            <div className="text-[10px] text-ink/40 mt-1 break-all">{previewData.email?.link}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-start">
                          <div data-testid="preview-sms" className="max-w-[85%] rounded-2xl rounded-bl-md bg-white border border-sand-200 px-3 py-2 text-[13px] text-ink whitespace-pre-wrap shadow-sm">{previewData.sms}</div>
                        </div>
                      )}
                      <p className="text-[10px] text-ink/40 mt-2">Sample {V.lc("person")} <b className="text-ink/55">{sampleName || "Alex"}</b> — each person sees their own first name.</p>
                    </div>
                  )}

                  {invText && texting && previewData?.sms && (
                    <MessageQualityHint text={previewData.sms} testId="import-sms-quality"
                      onClean={() => { setInviteMsg((m) => smartEncode(m)); toast.success("Cleaned up special characters for better delivery"); }} />
                  )}

                  {invEmail && emailing && (
                    <button type="button" data-testid="import-see-full-email" onClick={openFullPreview}
                      className="w-full rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:border-ink transition-colors inline-flex items-center justify-center gap-2">
                      <Eye className="h-4 w-4" /> See the exact email {V.lcs("person")} get
                    </button>
                  )}


                  <div className="rounded-xl border border-dashed border-sand-300 p-3">
                    <p className="text-xs font-semibold text-ink">Send a test to yourself first</p>
                    <p className="text-[11px] text-ink/45">Get the exact invite — check the wording, subject &amp; reply-to before the blast.</p>
                    <div className="mt-2 flex items-center gap-2">
                      <input data-testid="import-invite-test-to" value={testTo} onChange={(e) => { setTestTo(e.target.value); setTestSent(""); }}
                        placeholder="Enter your email address or mobile number"
                        className="flex-1 min-w-0 rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                      <button type="button" data-testid="import-invite-test-send" onClick={sendTest} disabled={testBusy}
                        className="shrink-0 rounded-full border border-ink/20 px-4 py-2 text-sm font-semibold text-ink hover:bg-sand-100 transition-colors disabled:opacity-50">{testBusy ? "Sending…" : "Send test"}</button>
                    </div>
                    <p className="text-[11px] text-ink/45 mt-1.5">
                      <Mail className="inline h-3 w-3 mr-1 -mt-0.5" /> Type an <b className="font-semibold">email address</b> to get the test email, or a <b className="font-semibold">mobile number</b> to get the test text{invText && invEmail ? " (we send whichever you enter)" : ""}. Leave it blank to use your account.
                    </p>
                    {testSent && <p data-testid="import-invite-test-sent" className="text-[11px] text-emerald-600 mt-1.5">✓ Test sent to {testSent}</p>}
                  </div>

                  <label className="flex items-center gap-2 text-xs text-ink/70 cursor-pointer">
                    <input type="checkbox" data-testid="import-invite-save-default" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} className="h-3.5 w-3.5 accent-ink" />
                    Save this{invEmail && emailing ? " message and subject" : " message"} as my {V.lc("org")}'s default
                  </label>
                </div>

                {/* 3 — Actions: skip (secondary, left) · send (primary, right) */}
                <div className="flex items-center justify-between gap-2 p-4 border-t border-sand-100 bg-sand-50/40">
                  <button data-testid="import-skip-invite" onClick={onClose}
                    className="rounded-full px-4 py-2.5 text-sm font-semibold text-ink/60 hover:text-ink hover:bg-sand-100 transition-colors">Skip for now — invite later</button>
                  <button data-testid="import-send-invites" onClick={sendInvites} disabled={busy || (!invText && !invEmail)}
                    className="rounded-full bg-redline-500 text-white px-6 py-2.5 text-sm font-semibold hover:bg-redline-600 transition-colors disabled:opacity-50">{busy ? "Sending…" : "Send invites"}</button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <button data-testid="import-done-close" onClick={onClose} className="rounded-full bg-ink text-white px-6 py-2.5 text-sm font-semibold hover:bg-ink-700 transition-colors">Done</button>
              </div>
            )}
          </div>
        )}
        {cropFile && (
          <LogoCropper file={cropFile} onCancel={() => setCropFile(null)}
            onCropped={(blob) => { setCropFile(null); uploadInviteLogo(blob); }} />
        )}
        <Dialog open={!!fullPreview} onOpenChange={(o) => { if (!o) setFullPreview(null); }}>
          <DialogContent className="max-w-2xl" data-testid="full-email-preview">
            <DialogHeader>
              <DialogTitle className="font-heading">This is exactly what {V.lcs("person")} get</DialogTitle>
            </DialogHeader>
            {fullBusy || !fullPreview?.html ? (
              <div className="py-10 text-center text-sm text-ink/50">{fullBusy ? "Building preview…" : "No preview yet"}</div>
            ) : (
              <div className="space-y-2">
                <div className="rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 text-[12px] space-y-0.5">
                  <div><span className="text-ink/40">From:</span> <b className="text-ink">{fullPreview.from_name}</b> <span className="text-ink/40">&lt;{fullPreview.from_email}&gt;</span></div>
                  <div><span className="text-ink/40">Subject:</span> <b className="text-ink">{fullPreview.subject}</b></div>
                  <div><span className="text-ink/40">Reply-to:</span> {fullPreview.reply_to || "none (no-reply)"}</div>
                </div>
                <iframe title="email-preview" data-testid="full-email-iframe" srcDoc={fullPreview.html}
                  sandbox="" className="w-full h-[420px] rounded-lg border border-sand-200 bg-white" />
                <p className="text-[11px] text-ink/40">Preview for sample {V.lc("person")} “{fullPreview.sample_name}”. Each {V.lc("person")} sees their own first name.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </DialogContent>
    </Dialog>
  );
}
